import Bowser from 'bowser';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {DrmServerType} from '../../../graphql/generated/types';
import {getEnv} from '../../env/env';
import {requestContext} from '../../requestContext';
import {
  closeVideoFullScreen,
  getIsFullScreen,
  getIsFullScreenEnabled,
  openVideoFullScreen,
} from '../utils/fullscreen';
import {VideoElement} from '../utils/VideoElement.interface';
import {INI} from './INI.interface';
import {INIPlayer, INIPlayerStartParam} from './INIPlayer.interface';
import {IniError} from './IniError.interface';
import {IniErrorCode} from './IniErrorCode.enum';
import {IniExtendedErrorCode} from './IniExtendedErrorCode.enum';
import {INIPlayerNetworkErrorObject} from './IniPlayerNetworkErrorObject.interface';
import {IniVideoPlayerProps} from './IniVideoPlayerProps.interface';

declare global {
  interface Window {
    INIPlayer: INI;
  }
}

const IniVideoPlayer = forwardRef<INIPlayer | null, IniVideoPlayerProps>(
  (
    {
      url,
      loop = false,
      controls = false,
      volume,
      muted = false,
      autoplay = false,
      width = '640px',
      height = '360px',
      playsinline = false,
      loadOnInitialPlay = false,
      useDrm = false,
      startTime,
      drmConfig,
      onReady,
      onPlay,
      onPlaying,
      onPause,
      onEnded,
      onTimeUpdate,
      onLoading,
      onVolumeChange,
      onFullScreenChange,
      onProgress,
      onError,
    },
    ref
  ) => {
    const iniPlayerRef = useRef<INIPlayer>();
    const containerRef = useRef<HTMLDivElement>(null);
    // iniPlayer.createPlayer()가 호출되어 iniPlayer 인스턴스가 생성되었는지 여부
    const [isIniInstanceCreated, setIsIniInstanceCreated] = useState(false);
    // 영상이 로딩된 후 처음 재생되는지 여부
    const [isInitialPlay, setIsInitialPlay] = useState(true);
    // 현재 브라우저 환경이 IniPlayer를 지원하는지 여부
    // DRM을 사용하는 경우 해당 브라우저 환경이 DRM을 지원하는지도 확인한다.
    const [isBrowserUnsupportIniPlayer, setIsBrowserUnsupportIniPlayer] =
      useState(false);
    const videoTypeRef = useRef<string>();
    const {isAndroid} = requestContext.requestInfo.display;

    // DRM 요청시에 필요한 작업 수행하는 함수
    const pageDrmRequestFilter = useCallback(
      (requestBody: any, xhr: XMLHttpRequest) => {
        if (typeof window === 'undefined' || !drmConfig) {
          return;
        }
        xhr.setRequestHeader(
          'Content-Type',
          'application/x-www-form-urlencoded'
        );
        const bytes = new Uint8Array(requestBody);
        const payload = window.INIPlayer.utils.array_to_base64(bytes);
        return `${drmConfig.token ? `token=${drmConfig.token}&` : ''}${drmConfig.provider ? `provider=${drmConfig.provider}&` : ''}payload=${payload}`;
      },
      [drmConfig]
    );

    // DRM 응답을 받아서 필요한 작업 수행하는 함수
    const pageDrmResponseFilter = useCallback((body: any) => {
      if (typeof window === 'undefined') {
        return;
      }
      const responseString = window.INIPlayer.utils.array_to_bstring(body);
      const responseJson = JSON.parse(responseString);
      const payload = responseJson.payload;
      return window.INIPlayer.utils.base64_to_array(payload).buffer;
    }, []);

    const iniDrmRequestFilter = useCallback(
      (requestBody: any, xhr: XMLHttpRequest, drmOptions: any) => {
        xhr.responseType = 'text';

        xhr.setRequestHeader(
          'Content-type',
          'application/x-www-form-urlencoded'
        );

        return (
          'spc=' +
          window.INIPlayer.utils.array_to_base64(requestBody) +
          '&assetId=' +
          drmOptions.assetID
        );
      },
      []
    );

    const iniDrmResponseFilter = useCallback((responseBody: any) => {
      let keyText = responseBody.trim();
      if (
        keyText.substr(0, 5) === '<ckc>' &&
        keyText.substr(-6, 6) === '</ckc>'
      ) {
        keyText = keyText.slice(5, -6);
      }
      return window.INIPlayer.utils.base64_to_array(keyText).buffer;
    }, []);

    // 비디오 로딩 시 넘겨줄 DRM 설정을 반환하는 함수
    const getDrmOptions = useCallback(() => {
      if (typeof window === 'undefined' || !drmConfig) {
        return;
      }
      return {
        widevine: {
          url: drmConfig.widevineLicenseUrl || undefined,
          ...(drmConfig.serverType === DrmServerType.Ini
            ? {
                customData: drmConfig.assertion,
                // 크롬에서 개발 편의성을 위해 로컬 환경에서는 무시합니다.
                ...(!getEnv().isLocal &&
                  isAndroid && {
                    video: {
                      // 안드로이드에서 security_level을 4(L1 기기만 대응)로 상향 했습니다.
                      // L1은 HW level에서 DRM을 적용하기 때문에 아래 설정을 추가합니다.
                      // https://kakaopagecorp.slack.com/archives/C048X4VRJ9H/p1744164986862839?thread_ts=1744097762.249989&cid=C048X4VRJ9H
                      robustness: 'HW_SECURE_ALL',
                    },
                  }),
              }
            : {
                requestFilter: pageDrmRequestFilter,
                responseFilter: pageDrmResponseFilter,
              }),
        },
        fairplay: {
          url: drmConfig.fairplayLicenseUrl || undefined,
          ...(drmConfig.serverType === DrmServerType.Ini
            ? {
                customData: drmConfig.assertion,
                getAssetID: function (initData: ArrayBuffer) {
                  const str =
                    window.INIPlayer.utils.utf16le_array_to_string(initData);
                  const slash = str.lastIndexOf('/');
                  return str.substring(slash + 1);
                },
                requestFilter: iniDrmRequestFilter,
                responseFilter: iniDrmResponseFilter,
              }
            : {
                getAssetID: (initData: ArrayBuffer) => {
                  const skdUri =
                    window.INIPlayer.utils.utf16le_array_to_string(initData);
                  return skdUri.substring(skdUri.indexOf('skd://') + 6);
                },
                requestFilter: pageDrmRequestFilter,
                responseFilter: pageDrmResponseFilter,
              }),
          certURL: drmConfig.fairplayCertificateUrl || undefined,
        },
      };
    }, [
      drmConfig,
      pageDrmRequestFilter,
      pageDrmResponseFilter,
      iniDrmRequestFilter,
      iniDrmResponseFilter,
      isAndroid,
    ]);

    // 최초 렌더링 시 비디오 지원여부 확인 및 iniPlayer 인스턴스 생성
    useEffect(() => {
      window.INIPlayer?.selectConfiguration({
        engine: 'html5',
        type: ['dash', 'hls'],
        ...(useDrm && {keysystem: ['widevine', 'fairplay']}),
      }).then(
        selected => {
          if (!containerRef.current) {
            return;
          }
          videoTypeRef.current = selected.type;
          // 이미 iniPlayer 인스턴스를 생성했다면 이전에 생성된 인스턴스를 삭제
          if (
            containerRef.current.getElementsByClassName(
              INI_PLAYER_INSTANCE_CLASSNAME
            )[0]
          ) {
            containerRef.current.removeChild(
              containerRef.current.getElementsByClassName(
                INI_PLAYER_INSTANCE_CLASSNAME
              )[0]
            );
          }
          iniPlayerRef.current = window.INIPlayer.createPlayer(selected);
          // ini Player에서 비디오 실제 크기로 px을 width, height에 박아넣기 떄문에, 직접 한 번 설정해준다.
          iniPlayerRef.current.style.width = '100%';
          iniPlayerRef.current.style.height = 'auto';
          iniPlayerRef.current.style.flex = '1';

          const videoElement = iniPlayerRef.current.getElementsByClassName(
            INI_VIDEO_PLAYER_CLASSNAME
          )[0] as VideoElement;
          if (videoElement) {
            const browser = Bowser.getParser(window.navigator.userAgent);
            // chromium 브라우저에서 화면분할 기능 사용 시 비디오 컨텐츠가 찌그러져 보이는 현상 해결을 위해 추가
            // 화면분할기능을 지원하는 os에만 부여한다.
            // 참고: https://bit.ly/3tyD90c
            if (
              browser.getOSName() === 'Android' &&
              parseFloat(browser.getOSVersion()) >=
                MIN_ANDROID_OS_VER_SUPPORT_SEPARATE_SCREEN
            ) {
              // @ts-expect-error
              videoElement.style['-webkit-border-radius'] = '1px';
            }
            // playsInline 속성은 iniPlayer 인스턴스가 생성될 때만 반영한다.
            // 이후 playsInline 속성이 변경되면 iniPlayer 인스턴스 생성부터 다시 수행한다.
            videoElement.playsInline = playsinline;
          }
          containerRef.current.appendChild(iniPlayerRef.current);
          // iniPlayer 인스턴스 생성 성공
          setIsIniInstanceCreated(true);
        },
        // 투입된 engine, type, keysystem 중 지원하는 게 없는 경우 reject되어 여기로 빠짐
        // 브라우저 옵션에서 보호된 콘텐츠 보기 옵션이 옵션이 꺼져있는 경우에도 여기로 빠짐
        // iniPlayer 인스턴스 생성에 실패함
        () => {
          setIsIniInstanceCreated(false);
          setIsBrowserUnsupportIniPlayer(true);
        }
      );
      return () => {
        setIsIniInstanceCreated(false);
        setIsInitialPlay(true);
        if (iniPlayerRef.current) {
          iniPlayerRef.current.destroy();
        }
      };
    }, [useDrm, playsinline]);

    useEffect(() => {
      // iniPlayer를 지원하지 않아 IniPlayer Element조차 생성할 수 없는 상황 (ex. IE 브라우저)
      if (isBrowserUnsupportIniPlayer) {
        onError?.({
          code: IniErrorCode.Custom,
          iniExtendedCode: IniExtendedErrorCode.UnsupportedBrowser,
          detail: 'Unsupported Browser',
          message: 'Unsupported Browser',
        });
      }
    }, [isBrowserUnsupportIniPlayer, onError]);

    // iniPlayer에 에러 핸들러 추가
    useEffect(() => {
      if (!iniPlayerRef.current || !isIniInstanceCreated) {
        return;
      }
      const iniPlayerEl = iniPlayerRef.current;
      const browser = Bowser.getParser(window.navigator.userAgent);
      const currentOsVersion = parseFloat(browser.getOSVersion());
      const isFairplayBugVersion =
        browser.getOSName() === 'iOS' &&
        currentOsVersion >= 15.0 &&
        currentOsVersion < 15.1;

      function errorHandler(event: Event) {
        let error: IniError = iniPlayerEl.error || {
          code: IniErrorCode.Custom,
          iniExtendedCode: IniExtendedErrorCode.Unknown,
          detail: 'unknown error',
          message: 'unknown error',
        };
        // HDCP문제로 재생을 못 하는 경우 사용자에게 정확한 정보를 보여주기 위해 더 명확한 커스텀 에러를 발생시킴
        if (iniPlayerEl?.iniStreaming?.hdcpRestricted) {
          error = {
            code: IniErrorCode.Custom,
            iniExtendedCode: IniExtendedErrorCode.UnsupportedHdcp,
            detail: 'Unsupported Hdcp',
            message: 'Unsupported Hdcp',
          };
        } else if (isFairplayBugVersion) {
          error = {
            code: IniErrorCode.Custom,
            iniExtendedCode: IniExtendedErrorCode.OsFairplayBugVersion,
            detail: 'Unsupport Fairplay DRM bug on iOS 15.0.x',
            message: 'Unsupport Fairplay DRM bug on iOS 15.0.x',
          };
        }
        onError?.(error, event);
      }
      iniPlayerEl.addEventListener('error', errorHandler);
      return () => {
        iniPlayerEl.removeEventListener('error', errorHandler);
      };
    }, [isIniInstanceCreated, onError]);

    // 받은 URL 및 DRM 정보로 비디오 로드
    const loadIniPlayer = useCallback(() => {
      if (
        typeof window === 'undefined' ||
        !url ||
        !iniPlayerRef.current ||
        !isIniInstanceCreated ||
        (useDrm && !drmConfig)
      ) {
        return;
      }
      const browser = Bowser.getParser(window.navigator.userAgent);
      iniPlayerRef.current.start(
        {
          // startTime이 0.01초 이하이거나 주어지지 않은 경우, 0.01초에서 시작하도록 한다.
          // 동영상 재생 전 첫 프레임을 보여줄 수 있게 하기위한 트릭. MediaFragments를 사용해 0.01초부터 동영상을 시작하도록 만든다.
          // 참고: https://caniuse.com/?search=Media%20Fragments%20
          url: `${url}#t=${!startTime || startTime <= 0.01 ? 0.01 : startTime}`,
          startTime,
          // iOS, iPadOS에서 FairPlay 사용시에는 MSE/EME를 사용할 수 없다. native 옵션은 강제로 MSE/EME를 사용하지 않도록 하는 옵션(INI 에서 확인해줌)
          native: browser.getOSName() === 'iOS',
          //
          enableSidxSeek: !(
            videoTypeRef.current === 'dash' ||
            videoTypeRef.current === 'restricted_dash'
          ),
          ...((useDrm && {drm: getDrmOptions()}) as Omit<
            INIPlayerStartParam,
            'url'
          >),
        },
        {
          errorHandlers: {
            drm: iniPlayerInternalErrorHandler,
            manifest: iniPlayerInternalErrorHandler,
            media: iniPlayerInternalErrorHandler,
          },
        }
      );
    }, [
      url,
      isIniInstanceCreated,
      useDrm,
      drmConfig,
      startTime,
      getDrmOptions,
    ]);

    // iniPlayer 인스턴스가 생성되는대로 비디오 로딩 시작
    // loadOnInitialPlay가 true인 경우 첫 플레이 시도 시 비디오 로딩을 시작해야 하므로
    // 인스턴스가 생성되어도 비디오 로딩을 하지 않는다.
    useEffect(() => {
      if (!loadOnInitialPlay && isIniInstanceCreated) {
        loadIniPlayer();
      }
    }, [loadOnInitialPlay, isIniInstanceCreated, loadIniPlayer]);

    // 외부에서 주입해준 props를 iniPlayer 인스턴스에 할당해준다.
    useEffect(() => {
      const video = iniPlayerRef.current;
      if (!video || !isIniInstanceCreated) {
        return;
      }
      video.muted = muted;
      video.loop = loop;
      if (volume !== undefined) {
        video.volume = volume;
      }
      video.autoplay = autoplay;
      video.controls = controls;
    }, [isIniInstanceCreated, autoplay, controls, loop, muted, volume]);

    // onReady 비디오 이벤트 핸들러 추가
    useEffect(() => {
      const video = iniPlayerRef.current;
      if (!video || !isIniInstanceCreated || !onReady) {
        return;
      }
      video.addEventListener('loadeddata', onReady);
      return () => {
        video.removeEventListener('loadeddata', onReady);
      };
    }, [isIniInstanceCreated, onReady]);

    // onPlay 비디오 이벤트 핸들러 추가
    useEffect(() => {
      const video = iniPlayerRef.current;
      if (!video || !isIniInstanceCreated || !onPlay) {
        return;
      }
      video.addEventListener('play', onPlay);
      return () => {
        video.removeEventListener('play', onPlay);
      };
    }, [isIniInstanceCreated, onPlay]);

    // onPlaying 비디오 이벤트 핸들러 추가
    useEffect(() => {
      const video = iniPlayerRef.current;
      if (!video || !isIniInstanceCreated || !onPlaying) {
        return;
      }
      video.addEventListener('playing', onPlaying);
      return () => {
        video.removeEventListener('playing', onPlaying);
      };
    }, [isIniInstanceCreated, onPlaying]);

    // onPause 비디오 이벤트 핸들러 추가
    useEffect(() => {
      const video = iniPlayerRef.current;
      if (!video || !isIniInstanceCreated || !onPause) {
        return;
      }
      video.addEventListener('pause', onPause);
      return () => {
        video.removeEventListener('pause', onPause);
      };
    }, [isIniInstanceCreated, onPause]);

    // onEnded 비디오 이벤트 핸들러 추가
    useEffect(() => {
      const video = iniPlayerRef.current;
      if (!video || !isIniInstanceCreated || !onEnded) {
        return;
      }
      video.addEventListener('ended', onEnded);
      return () => {
        video.removeEventListener('ended', onEnded);
      };
    }, [isIniInstanceCreated, onEnded]);

    // onTimeUpdate 비디오 이벤트 핸들러 추가
    useEffect(() => {
      const video = iniPlayerRef.current;
      if (!video || !isIniInstanceCreated || !onTimeUpdate) {
        return;
      }
      const videoEl = video.getElementsByClassName(
        INI_VIDEO_PLAYER_CLASSNAME
      )[0] as VideoElement;

      videoEl.addEventListener('timeupdate', onTimeUpdate);
      return () => {
        videoEl.removeEventListener('timeupdate', onTimeUpdate);
      };
    }, [isIniInstanceCreated, onTimeUpdate]);

    // onFullScreenChange 비디오 이벤트 핸들러 추가
    useEffect(() => {
      const video = iniPlayerRef.current;
      if (!video || !isIniInstanceCreated || !onFullScreenChange) {
        return;
      }
      const videoEl = video.getElementsByClassName(
        INI_VIDEO_PLAYER_CLASSNAME
      )[0] as VideoElement;

      videoEl.addEventListener('fullscreenchange', onFullScreenChange);
      return () => {
        videoEl.removeEventListener('fullscreenchange', onFullScreenChange);
      };
    }, [isIniInstanceCreated, onFullScreenChange]);

    // onVolumeChange 비디오 이벤트 핸들러 추가
    useEffect(() => {
      const video = iniPlayerRef.current;
      if (!video || !isIniInstanceCreated || !onVolumeChange) {
        return;
      }
      video.addEventListener('volumechange', onVolumeChange);
      return () => {
        video.removeEventListener('volumechange', onVolumeChange);
      };
    }, [isIniInstanceCreated, onVolumeChange]);

    // onLoading 비디오 이벤트 핸들러 추가
    useEffect(() => {
      const video = iniPlayerRef.current;
      if (!video || !isIniInstanceCreated) {
        return;
      }
      const onWaiting = () => {
        onLoading && onLoading(true);
      };
      const onCanPlayThrough = () => {
        onLoading && onLoading(false);
      };

      video.addEventListener('waiting', onWaiting);
      video.addEventListener('canplaythrough', onCanPlayThrough);
      return () => {
        video.removeEventListener('waiting', onWaiting);
        video.removeEventListener('canplaythrough', onCanPlayThrough);
      };
    }, [isIniInstanceCreated, onLoading]);

    // onIniLoaded 비디오 이벤트 핸들러 추가
    useEffect(() => {
      const video = iniPlayerRef.current;
      if (!video || !isIniInstanceCreated) {
        return;
      }
      const videoEl = video.getElementsByClassName(
        INI_VIDEO_PLAYER_CLASSNAME
      )[0] as VideoElement;

      const onIniLoaded = () => {
        // iniPlayer가 로딩된 이후 첫 재생이며 loadOnInitialPlay가 true인 경우,
        // 로딩이 끝남과 동시에 영상을 재생시켜야 하므로 videoEl.play()를 바로 호출해준다.
        if (isInitialPlay && loadOnInitialPlay) {
          setIsInitialPlay(false);
          videoEl.play();
        }
      };

      video.addEventListener('iniLoaded', onIniLoaded);
      return () => {
        video.removeEventListener('iniLoaded', onIniLoaded);
      };
    }, [isIniInstanceCreated, isInitialPlay, loadOnInitialPlay]);

    useEffect(() => {
      const video = iniPlayerRef.current;
      if (!video || !isIniInstanceCreated || !onProgress) {
        return;
      }

      video.addEventListener('progress', onProgress);
      return () => {
        video.removeEventListener('progress', onProgress);
      };
    }, [isIniInstanceCreated, onProgress]);

    useImperativeHandle<INIPlayer | null, INIPlayer | null>(ref, () => {
      const iniPlayer = iniPlayerRef.current || null;
      if (!iniPlayer || !isIniInstanceCreated) {
        return null;
      }
      const videoEl = iniPlayer.getElementsByClassName(
        INI_VIDEO_PLAYER_CLASSNAME
      )[0] as VideoElement;

      iniPlayer.getIsFullScreenAvailable = () =>
        getIsFullScreenEnabled(videoEl);

      // 현재 투입된 정보로 다시 시도해볼 수 있는 method 제공
      iniPlayer.reload = function () {
        iniPlayer.destroy().then(() => {
          loadIniPlayer();
        });
      };
      iniPlayer.isFullScreen = function () {
        return getIsFullScreen();
      };
      iniPlayer.openFullScreen = function () {
        openVideoFullScreen(videoEl);
      };
      iniPlayer.closeFullScreen = function () {
        closeVideoFullScreen();
      };
      // loadOnInitialPlay를 처리하기 위해 디폴트 play 함수를 override 한다.
      iniPlayer.play = async function () {
        // loadOnInitialPlay가 true인 경우는 metadata를 최초 재생시에 요청하는 경우이다. (일반적으로는 mpd, drm경로만 들어오면 요청 시작)
        // loadIniPlayer와 동시에 video play를 실행하면 비디오가 정상 실행되지 않으로, IniPlayer가 로드된 후에 play 되도록 한다.
        if (isInitialPlay) {
          if (loadOnInitialPlay) {
            loadIniPlayer();
          } else {
            videoEl.play();
            setIsInitialPlay(false);
          }
        } else {
          videoEl.play();
        }
      };
      return iniPlayer;
    }, [isIniInstanceCreated, isInitialPlay, loadOnInitialPlay, loadIniPlayer]);

    return (
      <div
        ref={containerRef}
        className="absolute inset-0 flex"
        style={{
          width,
          height,
        }}
      />
    );
  }
);

/**
 * INI Player 자체 네트워크 요청에러 시 핸들러.
 * 이 핸들러를 통해 로깅이나 retry, ignored, failed 여부를 지정할 수 있다.
 * 기본 설정은 return error.failed; 인 것으로 추정(바로 failed 이기 때문에 retry 없이 바로 에러 메시지를 보여주고있음)
 * @param error : 발생한 에러에 대한 객체
 * @param retryCount : error.retry 로 리턴했을 때의 카운트
 * @param startOptions : 플레이어 시작 객체. 디버깅용으로 쓰도록 넣어놓은 것 같다
 * @returns
 */
function iniPlayerInternalErrorHandler(
  error: INIPlayerNetworkErrorObject,
  retryCount: number
) {
  if (error.source === 'media') {
    if (retryCount >= MEDIA_RETRY_COUNT) {
      return error.failed;
    }
    return error.retry;
  }

  return error.failed;
}

const MIN_ANDROID_OS_VER_SUPPORT_SEPARATE_SCREEN = 7;
const MEDIA_RETRY_COUNT = 3;
const INI_VIDEO_PLAYER_CLASSNAME = 'iniplayer-media';
const INI_PLAYER_INSTANCE_CLASSNAME = 'iniplayer-element';

export {IniVideoPlayer};
